import React from 'react';
import {
  Button,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  notification,
} from 'antd';
import styled from 'styled-components';

import theme from 'assets/styles/theme';
import Loading from 'components/common/Loading';
import ScreenMask from 'components/common/ScreenMask';
import { Label } from 'components/common/styles';
import {
  COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
  DEFAULT_UNPACK_ADJUSTMENT_ITEM_QTY,
  LS_DEFAULT_WAREHOUSE_KEY,
  LOADING_ICON_SIZE1,
} from 'constants/config';
import { fetchWarehouseProductList } from 'services/inventory';
import { fetchWarehouseLocationList } from 'services/warehouse';
import { adjustPrepack } from 'services/unpack';
import { getWarehouseFromCache } from 'utils';

type Props = {
  onClose: Function;
  visible: boolean;
  warehouse: StringKAnyVPair;
};

const AdjustmentWrapper = styled.div`
  margin: 20px 0;

  & .err-msg-ctn,
  & .input-ctn {
    width: 50%;
  }

  & .err-msg-ctn {
    color: ${theme['@danger-color']};
  }

  & .input-prd-qty-ctn .ant-input-number-group-wrapper {
    width: 100%;
  }

  & .input-prd-qty-ctn .ant-input-number-handler-wrap {
    opacity: 1;
  }

  & .label-ctn {
    padding-right: 8px;
    text-align: right;
    width: 40%;
  }
`;

const locationDict: StringKAnyVPair = {};

const UnpackAdjustmentDialog = (props: Props) => {
  const { useState } = React;
  const [currentFromWarehouse, setCurrentFromWarehouse] = useState<StringKAnyVPair>({});
  const [fromLocation, setFromLocation] = useState<number>();
  //const [fromWarehouseList, setFromWarehouseList] = useState<StringKAnyVPair[]>();
  const [inited, setInited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [itemQty, setItemQty] = useState(1);
  const [toLocationOptions, setToLocationOptions] = useState<any[]>([]);
  const [skuInputVersion, setSkuInputVersion] = useState(0);
  const [skuItemList, setSkuItemList] = useState<StringKAnyVPair[]>([]);
  const [toLocation, setToLocation] = useState<number>();
  const [userError, setUserError] = useState<StringKAnyVPair>({});
  const [unpackAtSrcLocation, setUnpackAtSrcLocation] = useState(true);

  const skuSearchRef = React.useRef<any>(null);

  const adjust = async () => {
    const skuItem = skuItemList.find(e => e.locationNum === fromLocation);
    const dl = toLocationOptions.find(e => e.value === toLocation);
    if (skuItem && dl) {
      const param = {
        WarehouseNum: currentFromWarehouse.warehouseNum,
        SKU: skuItem.sku,
        SourceLocationCode: skuItem.locationCode,
        SourceLocationNum: skuItem.locationNum,
        DestinationLocationCode: dl.label,
        DestinationLocationNum: dl.value,
        UnpackQty: itemQty,
      };

      //console.log(param, skuItem);
      setIsLoading(true);

      try {
        const res = await adjustPrepack(param);

        //console.log('r', res);
        if (res) {
          if (typeof res === 'object') {
            if (res.error) {
              notification.error({
                message: `Error: ${res.error}`,
                duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
              });
            }
          } else if (res === true) {
            Modal.confirm({
              cancelText: 'No',
              okText: 'Yes',
              centered: true,
              title: 'Adjusted successfully! Do you want to adjust another?',
              onCancel() {
                onClose();
              },
              onOk() {
                setSkuInputVersion(skuInputVersion + 1);
                clearForm();
                setTimeout(() => {
                  focusPrepackSkuSearchBox();
                }, 0);
              },
            });
          }
        } else {
          notification.error({
            message: `Data format error: ${res}`,
            duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
          });
        }
      } catch(e) {
        //console.log('e:', e);
        notification.error({
          message: `Error: ${e}`,
          duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const clearForm = () => {
    //setSku('');
    setItemQty(1);
    setSkuItemList([]);
    setFromLocation(undefined);
    setToLocation(undefined);
  };

  const dialogFooter = () => {
    return (
      <Row justify="end">
        <Space>
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!isUnpackAvailable()}
            onClick={adjust}
            type="primary"
          >
            Submit
          </Button>
        </Space>
      </Row>
    );
  };

  const focusPrepackSkuSearchBox = () => {
    if (skuSearchRef.current) {
      skuSearchRef.current.focus();
    }
  };

  const fromLocationOptions = () => {
    const ld: StringKAnyVPair = {};
    const opts: StringKAnyVPair[] = [];

    skuItemList.forEach(e => {
      if (!ld[e.locationNum]) {
        ld[e.locationNum] = true;
        opts.push({
          label: e.locationCode,
          value: e.locationNum,
        });
      }
    });

    return opts;
  };

  const isAdjustQtyValid = () => {
    const skuItem = skuItemList.find(e => e.locationNum === fromLocation);

    return skuItem && itemQty <= skuItem.quantity;
  };

  const isUnpackAvailable = () => {
    return skuItemList.length > 0 && fromLocation && toLocation && isAdjustQtyValid();
  };

  // eslint-disable-next-line
  const loadInitialData = async () => {
    setIsLoading(true);

    try {
      const whNum = parseInt(
        localStorage.getItem(LS_DEFAULT_WAREHOUSE_KEY) || '-1'
      );
      //const wh = props.warehouse.warehouseId || 0;
      const wh = getWarehouseFromCache(whNum);

      if (!wh) {
        setIsLoading(false);
        return;
      }

      //console.log('wh', wh);
      setCurrentFromWarehouse(wh);

      const locations = locationDict[wh.warehouseNum] ? locationDict[wh.warehouseNum] : await fetchWarehouseLocationList(wh.warehouseId, true);

      //console.log('wh', wh, locations);
      if (Array.isArray(locations)) {
        const opts = locations.map(e => {
          return {
            label: e.locationCode,
            value: e.locationNum,
          };
        });

        if (!locationDict[wh.warehouseNum]) {
          locationDict[wh.warehouseNum] = locations;
        }

        setToLocationOptions(opts);
      }
    } finally {
      setIsLoading(false);
      focusPrepackSkuSearchBox();
    }
  };

  const maxAdjustQty = () => {
    const skuItem = skuItemList.find(e => e.locationNum === fromLocation);

    return skuItem ? skuItem.quantity : 1;
  };

  const onClose = () => {
    props.onClose();
  };

  const onFromLocationChange = (val: any) => {
    const skuItem = skuItemList.find(e => e.locationNum === val);

    setFromLocation(val);
    setToLocation(val);
    setUserError({
      ...userError,
      qtyError: undefined,
      qtyErrMsg: undefined,
    });

    if (skuItem) {
      if (skuItem.quantity < itemQty) {
        //console.log(skuItem.quantity, itemQty, skuItem.locationCode);
        setItemQty(DEFAULT_UNPACK_ADJUSTMENT_ITEM_QTY);

        if (skuItem.quantity < DEFAULT_UNPACK_ADJUSTMENT_ITEM_QTY) {
          setUserError({
            ...userError,
            qtyError: true,
            qtyErrMsg: `Insufficient quantity of "${skuItem.sku}" to unpack`,
          });
        }
      }
    }
  };

  const onPrepackSkuSearchBoxKeyDown = (evt: any) => {
    //console.log(evt.charCode, evt.keyCode);
    if (evt.keyCode === 9) {
      if (evt.target.value) {
        const str = evt.target.value.trim();

        if (str && typeof str === 'string') {
          searchPrepackSKU(str);
        }
      }
    }
  };

  const onQtyChange = (val: any) => {
    setItemQty(val);
  };

  const onToLocationChange = (val: any) => {
    setToLocation(val);
  };

  const onUnpackSourceLocationChange = (chk: boolean) => {
    setUnpackAtSrcLocation(chk);

    if (chk) {
      setToLocation(fromLocation);
    }
  };

  const searchPrepackSKU = async (val: string, evt?: any) => {
    const sku = val.trim();

    if (sku) {
      //console.log(val, evt);
      const queryParam = {
        $top: 30,
        $sortBy: 'sku asc',
        $calculateTotal: true,
        $count: true,
        uniqueCode: sku,
        productStatus: 0,
        hideZero: true,
        thumbnailSize: 50
      };

      setIsLoading(true);
      setUserError({});
      clearForm();

      try {
        const res = await fetchWarehouseProductList(currentFromWarehouse.warehouseId, queryParam);

        console.log('res', res);
        if (res && typeof res === 'object' && Array.isArray(res.data)) {
          if (res.data.length > 0) {
            const first = res.data[0];

            //setSku(sku);
            setSkuItemList([...res.data]);
            setFromLocation(first.locationNum);
            setToLocation(first.locationNum);
          } else {
            setUserError({
              skuError: true,
              skuErrMsg: `Insufficient inventory of "${sku}" to unpack`,
            });
          }
        }
      } catch(e) {
      } finally {
        setIsLoading(false);
      }
    }
  };

  React.useEffect(() => {
    if (!inited) {
      loadInitialData();
      setInited(true);
    }
  },[inited, loadInitialData]);

  return (<>
    <Modal
      centered
      footer={dialogFooter()}
      onCancel={onClose}
      maskClosable={false}
      title="Unpack Adjustment"
      visible={props.visible}
      width={600}
    >
      <AdjustmentWrapper>
        <Space direction="vertical" style={{width: '100%'}}>
          <Row align="middle">
            <div className="label-ctn">
              <Label className="label-bold tail-colon">Warehouse</Label>
            </div>
            <div className="input-ctn">
              <Label className="label-grey">{currentFromWarehouse.warehouseCode}</Label>
            </div>
          </Row>
          <Row align="middle">
            <div className="label-ctn">
              <Label className="label-bold tail-colon">Prepack SKU</Label>
            </div>
            <div className="input-ctn">
              <Input.Search
                allowClear
                key={`sku-input${skuInputVersion}`}
                onKeyDown={onPrepackSkuSearchBoxKeyDown}
                onSearch={searchPrepackSKU}
                ref={skuSearchRef}
              />
            </div>
          </Row>
          {userError.skuError && (
            <Row align="middle">
              <div className="label-ctn" />
              <div className="err-msg-ctn">{userError.skuErrMsg}</div>
            </Row>
          )}
          <Row align="middle">
            <div className="label-ctn">
              <Label className="label-bold tail-colon">Qty</Label>
            </div>
            <div className="input-ctn input-prd-qty-ctn">
              <InputNumber
                addonAfter={<Label className="label-grey">Max({maxAdjustQty()})</Label>}
                disabled={skuItemList.length === 0}
                onChange={onQtyChange}
                max={maxAdjustQty()}
                min={1}
                value={itemQty}
              />
            </div>
          </Row>
          {userError.qtyError && (
            <Row align="middle">
              <div className="label-ctn" />
              <div className="err-msg-ctn">{userError.qtyErrMsg}</div>
            </Row>
          )}
          <Row align="middle">
            <div className="label-ctn">
              <Label className="label-bold tail-colon">From Location</Label>
            </div>
            <div className="input-ctn">
              <Select
                disabled={skuItemList.length === 0}
                onChange={onFromLocationChange}
                options={fromLocationOptions()}
                style={{ width: '100%' }}
                value={fromLocation}
              />
            </div>
          </Row>
          <Row align="middle">
            <div className="label-ctn">
              <Label className="label-bold tail-colon">Unpack at Source Location</Label>
            </div>
            <div className="input-ctn">
              <Switch
                disabled={skuItemList.length === 0}
                checked={unpackAtSrcLocation}
                checkedChildren={'Yes'}
                onChange={onUnpackSourceLocationChange}
                unCheckedChildren={'No'}
              />
            </div>
          </Row>
          <Row align="middle">
            <div className="label-ctn">
              <Label className="label-bold tail-colon">To Location</Label>
            </div>
            <div className="input-ctn">
              <Select
                disabled={skuItemList.length === 0 || unpackAtSrcLocation}
                filterOption={(value: string, option: any)=> {return option.label.toLowerCase().indexOf(value.toLowerCase()) >= 0}}
                onChange={onToLocationChange}
                options={toLocationOptions}
                showSearch
                style={{ width: '100%' }}
                value={toLocation}
              />
            </div>
          </Row>
        </Space>
      </AdjustmentWrapper>
    </Modal>
    {isLoading && (
      <ScreenMask>
        <Loading size={LOADING_ICON_SIZE1} />
      </ScreenMask>
    )}
  </>);
};

export default UnpackAdjustmentDialog;
